import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './marketing.css';

const PLANS = [
  {
    name: 'Starter', price: '$3,500', sub: '/ month', color: '#4f8ef7',
    desc: 'Perfect for small businesses automating their first workflow.',
    features: ['2 automation workflows built & maintained', 'AI chatbot integration (1 channel)', 'Dedicated AI engineer', 'Email + Slack support', 'Monthly performance report', 'Free onboarding audit included', '3-month minimum engagement'],
    cta: 'Get Started', outline: true,
  },
  {
    name: 'Professional', price: '$7,500', sub: '/ month', color: '#7c3aed', badge: 'Most Popular',
    desc: 'For growing businesses ready to automate multiple departments.',
    features: ['Up to 5 automation workflows', 'Custom AI chatbot (trained on your data)', 'Dedicated AI engineer', 'Priority same-day support', 'Weekly strategy calls', 'Live analytics dashboard', 'API integrations (unlimited)', '3-month minimum engagement'],
    cta: 'Start Automating', outline: false,
  },
  {
    name: 'Scale', price: '$15,000', sub: '/ month', color: '#4f8ef7',
    desc: 'For companies serious about full AI transformation.',
    features: ['Unlimited automation workflows', 'Custom AI SaaS product build', '2 engineers + project manager', 'Trading strategy automation included', '4-hour SLA response time', 'Quarterly exec strategy sessions', 'White-label chatbot suite', '3-month minimum engagement'],
    cta: 'Talk to Us', outline: false,
  },
  {
    name: 'Enterprise', price: 'Custom', sub: 'tailored to you', color: '#7c3aed',
    desc: 'Full AI transformation for large organisations.',
    features: ['Everything in Scale', 'Dedicated engineering team (4+)', 'On-site workshops & training', 'Full AI transformation roadmap', 'Custom NDA & SLA contracts', '24/7 priority hotline', 'Co-development partnerships'],
    cta: 'Contact Us', outline: true,
  },
];

const FAQS = [
  { q: 'Is there a setup fee?', a: 'No setup fees. Your monthly retainer covers everything — building, deploying, monitoring and maintaining your automations.' },
  { q: 'What is the minimum contract?', a: 'We ask for a 3-month initial engagement so we can build, test and optimise properly. After that it\'s month-to-month.' },
  { q: 'How quickly will I see results?', a: 'Most automations are live within 2–4 weeks. ROI is typically visible within the first 30–60 days.' },
  { q: 'Can I upgrade my plan?', a: 'Yes — upgrade anytime. We\'ll migrate your existing automations and expand capacity from the next billing cycle.' },
  { q: 'What\'s included in the free audit?', a: 'A 30-minute strategy call where we map your workflows, identify the top 3 automation opportunities, and give you an ROI estimate. No commitment required.' },
];

export default function Pricing() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="mkt-root">
      <Helmet>
        <title>AI Automation Pricing | Gantima — Plans from $3,500/month</title>
        <meta name="description" content="Gantima AI automation pricing. Starter from $3,500/month, Professional from $7,500/month, Scale from $15,000/month, Enterprise custom. All plans include a dedicated AI engineer and free onboarding audit." />
        <meta name="keywords" content="AI automation pricing, AI agency pricing, automation service cost, AI chatbot pricing, workflow automation cost, how much does AI automation cost, gantima pricing" />
        <link rel="canonical" href="https://www.gantima.com/pricing" />
        <meta property="og:title" content="AI Automation Pricing | Gantima — From $3,500/month" />
        <meta property="og:description" content="Transparent pricing for AI automation services. Starter $3,500/mo, Professional $7,500/mo, Scale $15,000/mo, Enterprise custom." />
        <meta property="og:url" content="https://www.gantima.com/pricing" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": FAQS.map(f => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a }
          }))
        })}</script>
      </Helmet>

      {/* Nav */}
      <nav className="mkt-nav">
        <a href="/" className="mkt-nav-brand" onClick={e => { e.preventDefault(); navigate('/'); }}>
          <img src="/GANTIMA-Logo.png" alt="Gantima" className="mkt-nav-logo-left-img" />
          <span className="mkt-nav-brand-tag">SOFTWARE AUTOMATION</span>
        </a>
        
        <ul className="mkt-nav-links">
          <li><a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>Home</a></li>
          <li><a href="/services" onClick={e => { e.preventDefault(); navigate('/services'); }}>Services</a></li>
          <li><a href="/pricing" onClick={e => { e.preventDefault(); navigate('/pricing'); }}>Pricing</a></li>
          <li className="mkt-nav-dropdown">
            <span className="mkt-nav-dropdown-trigger">Products ▾</span>
            <div className="mkt-nav-dropdown-menu">
              <a href="https://www.viralvolt.ai/" target="_blank" rel="noopener noreferrer" className="mkt-nav-dropdown-featured">
                <div>
                  <div className="mkt-nav-dropdown-name">ViralVolt.ai</div>
                  <div className="mkt-nav-dropdown-desc">AI Social Media Marketing Tool</div>
                </div>
              </a>
              <div className="mkt-nav-dropdown-label">Solutions by Industry</div>
              <div className="mkt-nav-dropdown-grid">
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>E-Commerce</a>
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>HealthTech</a>
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>FinTech & Insurance</a>
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>Algorithmic Trading</a>
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>Crypto & DeFi</a>
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>Legal & Compliance</a>
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>Real Estate</a>
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>HR & Recruiting</a>
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>Logistics & Supply Chain</a>
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>SaaS & Tech</a>
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>Hedge Funds & Prop Trading</a>
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>Forex & Futures</a>
                <a href="https://www.viralvolt.ai/" target="_blank" rel="noopener noreferrer" className="viralvolt-link">Social Media Marketing</a>
              </div>
            </div>
          </li>
              <li><a href="/about" onClick={e => { e.preventDefault(); navigate('/about'); }}>About</a></li>
          <li><a href="/careers" onClick={e => { e.preventDefault(); navigate('/careers'); }}>Careers</a></li>
        </ul>
        <div className="mkt-nav-actions">
          <button className="mkt-btn mkt-btn-primary mkt-btn-glow" onClick={() => navigate('/')}>Book Free Consultation</button>
        </div>
      </nav>

      {/* Hero */}
      <section style={{ padding: '130px 5% 80px', background: '#020817', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', width: '500px', height: '500px', borderRadius: '50%', background: 'rgba(124,58,237,0.08)', filter: 'blur(80px)', top: '-80px', left: '50%', transform: 'translateX(-50%)', pointerEvents: 'none' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div className="mkt-pill" style={{ marginBottom: '20px' }}>TRANSPARENT PRICING</div>
          <h1 style={{ fontSize: 'clamp(2.2rem,4.5vw,3.6rem)', fontWeight: 900, color: '#f1f5f9', lineHeight: 1.1, marginBottom: '20px' }}>
            Simple, Honest Pricing<br /><span className="mkt-gradient-text">No Hidden Fees. Ever.</span>
          </h1>
          <p style={{ fontSize: '1.1rem', color: '#94a3b8', maxWidth: '560px', margin: '0 auto', lineHeight: 1.75 }}>
            Every plan includes a dedicated AI engineer, ongoing maintenance, and a free onboarding audit. Cancel anytime after 3 months.
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="mkt-section" style={{ background: '#030d1d' }}>
        <div className="mkt-container">
          <div className="mkt-pricing-grid" style={{ maxWidth: '1200px', margin: '0 auto 24px' }}>
            {PLANS.map((plan, i) => (
              <div key={i} className={`mkt-pricing-card${plan.badge ? ' mkt-pricing-featured' : ''}`}>
                {plan.badge && <div className="mkt-pricing-badge">{plan.badge}</div>}
                <div className="mkt-pricing-name">{plan.name}</div>
                <p style={{ fontSize: '.85rem', color: '#64748b', margin: '0 0 8px' }}>{plan.desc}</p>
                <div className="mkt-pricing-price">
                  <span className="mkt-price-amount mkt-gradient-text">{plan.price}</span>
                  <span className="mkt-price-sub">{plan.sub}</span>
                </div>
                <ul className="mkt-pricing-features">
                  {plan.features.map((f, j) => (
                    <li key={j}><span className="mkt-check">✓</span>{f}</li>
                  ))}
                </ul>
                <button
                  className={`mkt-btn mkt-btn-lg${plan.outline ? ' mkt-btn-outline' : ' mkt-btn-primary'}`}
                  style={{ width: '100%', justifyContent: 'center' }}
                  onClick={() => navigate('/')}
                >{plan.cta}</button>
              </div>
            ))}
          </div>
          <p className="mkt-pricing-note">All prices in USD. Minimum 3-month engagement. Month-to-month after initial period.</p>
        </div>
      </section>

      {/* FAQ */}
      <section className="mkt-section" style={{ background: '#020817' }}>
        <div className="mkt-container">
          <div className="mkt-section-header">
            <div className="mkt-pill">PRICING FAQ</div>
            <h2>Common Questions About Pricing</h2>
          </div>
          <div className="mkt-faq-list">
            {FAQS.map((f, i) => (
              <div key={i} className={`mkt-faq-item${openFaq === i ? ' mkt-faq-open' : ''}`}>
                <button className="mkt-faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  {f.q}<span className="mkt-faq-icon">{openFaq === i ? '−' : '+'}</span>
                </button>
                {openFaq === i && <div className="mkt-faq-a"><p>{f.a}</p></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mkt-cta-banner">
        <div className="mkt-cta-orb mkt-cta-orb-1" /><div className="mkt-cta-orb mkt-cta-orb-2" />
        <div className="mkt-cta-inner">
          <div className="mkt-cta-badge">🎯 FREE AUDIT</div>
          <h2>Start With a Free Audit</h2>
          <p>Not sure which plan is right? Book a free 30-minute call and we'll tell you exactly what's possible for your business — with a custom ROI estimate.</p>
          <div className="mkt-cta-actions">
            <button className="mkt-btn mkt-btn-primary mkt-btn-xl mkt-btn-glow" onClick={() => navigate('/')}>Book Free Strategy Call</button>
            <span className="mkt-cta-note">Free · No commitment · Results in 60 days</span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="mkt-footer">
        <div className="mkt-container">
          <div className="mkt-footer-grid">
            <div className="mkt-footer-brand">
              <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }} style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'14px', textDecoration:'none' }}>
                <img src="/GANTIMA-Logo.png" alt="Gantima" style={{ height:'48px', width:'auto', objectFit:'contain', filter:'drop-shadow(0 0 8px rgba(79,142,247,0.4))', flexShrink:0 }} />
                <span style={{ fontSize:'.55rem', fontWeight:800, color:'#4f8ef7', letterSpacing:'2.5px', textTransform:'uppercase', borderLeft:'1px solid rgba(79,142,247,0.25)', paddingLeft:'10px', lineHeight:1.5 }}>SOFTWARE<br/>AUTOMATION</span>
              </a>
              <p>AI automation agency helping businesses eliminate manual work and scale intelligently.</p>
              <a href="mailto:support@gantima.com" className="mkt-footer-email">support@gantima.com</a>
            </div>
            <div className="mkt-footer-col"><h4>Services</h4><ul>
              <li><a href="/services" onClick={e => { e.preventDefault(); navigate('/services'); }}>AI Chatbot Development</a></li>
              <li><a href="/services" onClick={e => { e.preventDefault(); navigate('/services'); }}>Workflow Automation</a></li>
              <li><a href="/services" onClick={e => { e.preventDefault(); navigate('/services'); }}>Trading Bot</a></li>
            </ul></div>
            <div className="mkt-footer-col"><h4>Company</h4><ul>
              <li><a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>Home</a></li>
              <li><a href="https://www.viralvolt.ai/" target="_blank" rel="noopener noreferrer">ViralVolt.ai ⚡</a></li>
              <li><a href="/about" onClick={e => { e.preventDefault(); navigate('/about'); }}>About</a></li>
              <li><a href="/careers" onClick={e => { e.preventDefault(); navigate('/careers'); }}>Careers</a></li>
            </ul></div>
            <div className="mkt-footer-col"><h4>Contact</h4><ul>
              <li><a href="mailto:support@gantima.com">support@gantima.com</a></li>
            </ul></div>
          </div>
          <div className="mkt-footer-bottom">
            <span>© 2026 Gantima Software. All rights reserved.</span>
            <a href="/privacy" onClick={e => { e.preventDefault(); navigate('/privacy'); }} style={{ color: '#64748b' }}>Privacy Policy</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
